import { Button, Card, CardActionArea, CardActions, CardContent, CardHeader, CardMedia, Chip, Link, Skeleton, Typography } from '@mui/material';
import ArticleIcon from '@mui/icons-material/Article';
import { formatDate } from '../../../utils/formatDate';
import { generatePath } from '../../../utils/generatePath';
import { PAGE } from '../../../constants/page';

interface Props {
  children?: React.ReactElement<unknown>;
  /** newsコンテンツ */
  news?: News
  /** 読み込み中 */
  isLoading?: boolean
  /** 高さ */
  height?: string,
}

export default function News(props: Props) {
  if (props.isLoading) {
    return (
      <>
        <CardActionArea>
          <Skeleton variant="rectangular" animation="wave" height={'150px'} />
          <Skeleton animation="wave" width={'5rem'} height={'1em'} />
          <Skeleton animation="wave" width={'10rem'} height={'1em'} />
          <CardContent sx={{ height: props.height || 'auto', overflow: 'hidden' }}>
            <Skeleton animation="wave" width={'100%'} height={'2em'} />
            <Skeleton animation="wave" width={'100%'} height={'2em'} />
            <Skeleton animation="wave" width={'100%'} height={'2em'} />
          </CardContent>
        </CardActionArea>
      </>
    )
  } else {
    return (
      <Card>
        <Link
          href={generatePath(PAGE.NEWS_DETAIL.PATH, props.news?.id)}
          sx={{ textDecoration: 'none' }}
        >
          <CardActionArea
            sx={(theme) => ({
              '.card-media': {
                height: 0,
                [theme.breakpoints.up('md')]: {
                  height: 250,
                },
                objectFit: 'contain',
                overflow: 'hidden',
                transition: '0.3s',
                backgroundSize: 'auto 250px',
                backgroundColor: theme.palette.background.paper,
              },
              '&:hover .card-media': {
                backgroundSize: 'auto 300px',
              },
              '.card-title': {
                position: 'relative',
                fontWeight: 'bold',
                width: 'fit-content',
                marginBottom: '1rem',
                color: theme.palette.grey[900],
                '&.active': {
                  '&::after': {
                    transform: 'scale(1, 1)'
                  }
                },
                '&:after': {
                  position: 'absolute',
                  left: 0,
                  content: `''`,
                  width: '100%',
                  height: 4,
                  background: theme.palette.grey[900],
                  bottom: -1,
                  transform: 'scale(0, 1)',
                  transformOrigin: 'left top',
                  transition: 'transform 0.3s'
                }
              },
              '&:hover .card-title': {
                '&::after': {
                  transform: 'scale(1, 1)'
                }
              }
            })}>
            <CardMedia
              className="card-media"
              image={props.news?.thumbnail.url}
            />
            <CardHeader
              sx={(theme) => ({
                paddingBottom: 0
              })}
              title={props.news?.tags.map((t, i) => <Chip sx={{ marginRight: '0.25rem', marginBottom: '0.5rem' }} key={i} label={t} variant="outlined" />)}
              subheader={formatDate(props.news?.date)}
            />
            <CardContent sx={{ height: props.height || 'auto', overflow: 'hidden' }}>
              <Typography
                className='card-title'
                sx={(theme) => ({
                  fontSize: '1.25rem',
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 1,
                  marginBottom: '1rem'
                })}
                variant="h5"
              >
                {props.news?.title}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={(theme) => ({
                  fontSize: '1rem',
                  minHeight: '2.5em',
                  lineHeight: '1.25em',
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 2,
                })}
              >
                {props.news?.summary}
              </Typography>
            </CardContent>
          </CardActionArea>
        </Link>
      </Card >
    )
  }
}